import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Send, RefreshCw, CheckCircle2, XCircle, Plug, CalendarClock } from 'lucide-react';
import ClearviewDialog from './ClearviewDialog';
import DataFeedsDialog from './DataFeedsDialog';
import GoogleSheetDialog from './GoogleSheetDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function weekLabel(w) {
  const d = new Date(w + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export default function KPICoverage({ onAddWeek }) {
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [cvOpen, setCvOpen] = useState(false);
  const [feedsOpen, setFeedsOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cov, integ] = await Promise.all([
        axios.get(`${API_URL}/kpi/coverage`, { params: { weeks: 8 } }),
        axios.get(`${API_URL}/integrations/status`).catch(() => ({ data: null })),
      ]);
      setData(cov.data);
      setIntegrations(integ.data);
    } catch { toast.error('Could not load KPI coverage'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remind = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/kpi/coverage/remind`);
      toast.success(res.data.missing === 0 ? `All stores reported for w/e ${res.data.week}` : `Owners emailed: ${res.data.missing} store(s) missing`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reminder failed'); }
    finally { setSending(false); }
  };

  if (loading) return <Card className="border border-slate-200 rounded-lg"><CardContent className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></CardContent></Card>;
  if (!data) return null;

  const missing = data.missing_latest.length;
  const connected = integrations?.clearview?.configured || integrations?.xenial?.configured;

  return (
    <Card className="border border-slate-200 rounded-lg" data-testid="kpi-coverage-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <CalendarClock className="w-4 h-4 text-orange-500" /> Data routine · last 8 weeks
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Week ending <b>{data.latest_week}</b>: {missing === 0 ? 'every store has reported' : `${missing} store${missing === 1 ? '' : 's'} missing`} · {data.coverage_pct}% coverage.
              Owners get a missing-stores email every Tuesday 9am.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} className="rounded-sm border-slate-200" data-testid="coverage-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
            <Button size="sm" onClick={remind} disabled={sending} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="coverage-remind-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}Email owners the gaps now
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="coverage-matrix">
            <thead>
              <tr className="text-slate-400">
                <th className="text-left font-medium py-1 pr-3">Store</th>
                {data.weeks.map(w => <th key={w} className={`font-medium py-1 px-1 ${w === data.latest_week ? 'text-slate-900' : ''}`}>{weekLabel(w)}</th>)}
                <th className="text-left font-medium py-1 pl-3">Manager</th>
              </tr>
            </thead>
            <tbody>
              {data.stores.map(s => (
                <tr key={s.store_id} className="border-t border-slate-100" data-testid={`coverage-row-${s.store_id}`}>
                  <td className="py-1.5 pr-3 font-medium text-slate-800 whitespace-nowrap">{s.store_name}</td>
                  {data.weeks.map(w => (
                    <td key={w} className="py-1.5 px-1 text-center">
                      {s.weeks[w]
                        ? <CheckCircle2 className="w-4 h-4 text-green-500 inline" />
                        : <button type="button" title={`Add ${s.store_name} · w/e ${w}`} onClick={() => onAddWeek?.(s.store_id, w)} className="inline-flex" data-testid={`coverage-missing-${s.store_id}-${w}`}><XCircle className={`w-4 h-4 ${w === data.latest_week ? 'text-red-400' : 'text-slate-300'} hover:text-orange-500`} /></button>}
                    </td>
                  ))}
                  <td className="py-1.5 pl-3 text-slate-500 whitespace-nowrap">{s.managers.map(m => m.name).join(', ') || <span className="text-amber-600">unassigned</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="integration-status">
          <Plug className="w-3.5 h-3.5" />
          <span className="font-medium text-slate-700">Automated feeds:</span>
          <button type="button" onClick={() => setCvOpen(true)} className={`px-2 py-0.5 rounded-full underline-offset-2 hover:underline ${integrations?.clearview?.configured ? 'bg-green-100 text-green-700' : 'bg-slate-100'}`} data-testid="clearview-open-btn">Clearview {integrations?.clearview?.configured ? 'connected · nightly 2am' : 'not connected · set up'}</button>
          <ClearviewDialog open={cvOpen} onOpenChange={setCvOpen} />
          <button type="button" onClick={() => setFeedsOpen(true)} className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 hover:underline underline-offset-2" data-testid="data-feeds-open-btn">Email &amp; agent feeds · set up</button>
          <DataFeedsDialog open={feedsOpen} onOpenChange={setFeedsOpen} />
          <button type="button" onClick={() => setSheetOpen(true)} className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 hover:underline underline-offset-2" data-testid="gsheet-open-btn">Google Sheet · set up</button>
          <GoogleSheetDialog open={sheetOpen} onOpenChange={setSheetOpen} />
          <span className={`px-2 py-0.5 rounded-full ${integrations?.xenial?.configured ? 'bg-green-100 text-green-700' : 'bg-slate-100'}`}>Xenial {integrations?.xenial?.configured ? 'connected' : 'not connected'}</span>
          {!connected && <span>— until Clearview is connected, head office enters or uploads weekly (click any ✕ to add that week).</span>}
        </div>
      </CardContent>
    </Card>
  );
}
